import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../lib/translations';
import Section from '../components/UI/Section';
import { ExternalLink, Headphones, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';

const Resources = () => {
  const { lang } = useLanguage();
  const t = translations[lang];

  const content = {
    da: {
      title: "Ressourcer",
      intro: "Her finder du links til andre ressourcer samt kommende guidede meditationer, der kan støtte dig i din mindfulness- og compassion-træning.",
      audio_title: "Lydfiler",
      audio_coming_soon: "Nye lydoptagelser er på vej",
      audio_coming_soon_desc: "Jeg er i gang med at forberede nye guidede meditationer og øvelser til dig. Hold øje med siden for opdateringer.",
      links_title: "Links til andre ressourcer",
      links_items: [
        {
          title: "Balanced Minds",
          description: "On the UK Balanced Minds website, you will find a variety of resources in English designed to support you developing self-compassion.",
          url: "https://balancedminds.com/compassion-focused-therapy-resources/",
          linkText: "Besøg Balanced Minds"
        },
        {
          title: "Compassionate Mind Foundation",
          description: "The Compassionate Mind Foundation also has many resources in English.",
          url: "https://www.compassionatemind.co.uk/",
          linkText: "Besøg The Compassionate Mind Foundation"
        }
      ]
    },
    en: {
      title: "Resources",
      intro: "Here you will find links to other resources and upcoming guided meditations that can support you in your mindfulness and compassion training.",
      audio_title: "Audio Files",
      audio_coming_soon: "New audio recordings are coming soon",
      audio_coming_soon_desc: "I am currently preparing new guided meditations and exercises for you. Please check back soon for updates.",
      links_title: "Links to other resources",
      links_items: [
        {
          title: "Balanced Minds",
          description: "On the UK Balanced Minds website, you will find a variety of resources in English designed to support you developing self-compassion.",
          url: "https://balancedminds.com/compassion-focused-therapy-resources/",
          linkText: "Visit Balanced Minds"
        },
        {
          title: "Compassionate Mind Foundation",
          description: "The Compassionate Mind Foundation also has many resources in English.",
          url: "https://www.compassionatemind.co.uk/",
          linkText: "Visit The Compassionate Mind Foundation"
        }
      ]
    }
  };

  const c = content[lang];
  const bgImageUrl = "https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/img-2272-1.jpeg";

  return (
    <div className="pt-12 md:pt-16 lg:pt-20">
      {/* Hero Section with Wide-Angle Photo Background */}
      <section className="relative pt-16 md:pt-20 lg:pt-24 pb-16 md:pb-20 lg:pb-24 overflow-hidden min-h-[260px] md:min-h-[320px] lg:min-h-[360px] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src={bgImageUrl} 
            alt="Copenhagen skyline" 
            className="w-full h-full object-cover object-[center_75%]"
          />
        </div>

        <div className="container-custom relative z-10 w-full">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-xl text-left mr-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium text-white tracking-tight">
              {c.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Audio Files Section */}
      <Section className="bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-12 h-12 bg-[#c16e5e]/10 rounded-full flex items-center justify-center">
              <Headphones className="w-6 h-6 text-[#c16e5e]" />
            </div>
            <h2 className="text-3xl font-serif text-stone-900">{c.audio_title}</h2>
          </div>

          {/* Beautiful placeholder card for upcoming recordings */}
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-stone-50 rounded-2xl p-8 md:p-12 border border-stone-100 text-center max-w-2xl mx-auto flex flex-col items-center"
          >
            <div className="w-16 h-16 bg-white rounded-full shadow-sm flex items-center justify-center mb-6 text-[#c16e5e]">
              <Calendar className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-serif text-stone-900 mb-3">
              {c.audio_coming_soon}
            </h3>
            <p className="text-stone-600 leading-relaxed max-w-md">
              {c.audio_coming_soon_desc}
            </p>
          </motion.div>
        </div>
      </Section>

      {/* Links Section */}
      <Section className="bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-12 h-12 bg-[#c16e5e]/10 rounded-full flex items-center justify-center">
              <ExternalLink className="w-6 h-6 text-[#c16e5e]" />
            </div>
            <h2 className="text-3xl font-serif text-stone-900">{c.links_title}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {c.links_items.map((link, i) => (
              <a 
                key={i}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-stone-100 hover:shadow-md transition-all duration-300 group flex flex-col justify-between"
              >
                <div>
                  <h3 className="text-xl md:text-2xl font-serif text-stone-900 mb-3 group-hover:text-[#c16e5e] transition-colors">
                    {link.title}
                  </h3>
                  <p className="text-stone-600 leading-relaxed mb-6 text-sm md:text-base">
                    {link.description}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-[#c16e5e] font-medium mt-auto">
                  {link.linkText} 
                  <ExternalLink className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Resources;
